import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { ISSUES_MALWARE } from '../../../../../shared/featureFlags/flagNames';
import { IssueFilterKeys, type VulnIssueType } from '../../../../../shared/issuesV2';
import { useIsFeatureEnabled } from '../../../../helpers/features/featureFlags';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../../redux/savedFilters/hooks';
import { FilterPanel } from '../../../generic/Filter';
import FilterCardCheckboxes from '../../../generic/Filter/FilterCardCheckboxes';
import useFetchIssuesAction from '../../IssuesInbox/hooks/useFetchIssuesAction';
import type { SelectOption } from '../../../../types';
import type { SelectedOptionsProps } from '../../../generic';

const BASE_FILTER_OPTIONS: SelectOption[] = [
  { value: 'vulnerability', label: 'Vulnerability' },
];

const VulnTypeFilter: VoidFunctionComponent<Omit<SelectedOptionsProps, 'onSelect'>> = ({ selectedOptions }) => {
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const setFilterHasChanged = useSetFilterHasChanged();
  const isMalwareEnabled = useIsFeatureEnabled(ISSUES_MALWARE);

  const FILTER_OPTIONS = useMemo(() => {
    const options = [...BASE_FILTER_OPTIONS];
    if (isMalwareEnabled) {
      options.push({ value: 'malware', label: 'Malicious Dependency' });
    }
    return options;
  }, [isMalwareEnabled]);

  // Only show the filter if malware is enabled (otherwise there's only one type)
  if (!isMalwareEnabled) {
    return null;
  }

  const selectedCheckboxes = (selectedOptions ?? []).map((option: string) => ({
    label: FILTER_OPTIONS.find(filterOption => filterOption.value === option)?.label || option,
    value: option,
  }));

  const onCheckboxChange = (newSelected: SelectOption[]) => {
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.Type]: newSelected.map(({ value }) => value as VulnIssueType),
    });
    setFilterHasChanged(true);
  };

  return (
    <FilterPanel>
      <FilterCardCheckboxes
        label="Issue type"
        options={FILTER_OPTIONS}
        values={selectedCheckboxes}
        onChange={onCheckboxChange}
        testId="issues-vuln-type-filters"
      />
    </FilterPanel>
  );
};

export default VulnTypeFilter;
